import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {
  UnsavedChangesDialogComponent
} from "../components/util/design/unsavedChangesDialog/unsaved-changes-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {NotificationsService} from "../services/notifications/notifications.service";

/**
 * Ergebnis von `discardChanges()` beim Verlassen per Dialog. Komponenten ohne Rückgabewert (`void`)
 * gelten als `'discarded'` – der bisherige Vertrag bleibt gültig.
 * - `'savedAccepted'`: einzeln übernommene Änderungen (chatBIC-Vorschlag) werden gespeichert, der Rest verworfen.
 * - `'blocked'`: das Speichern der übernommenen Änderungen ist nicht möglich (z. B. Pflichtfeld fehlt) –
 *   die Navigation bleibt aus, damit sie nicht still verloren gehen.
 */
export type DiscardOutcome = 'discarded' | 'savedAccepted' | 'blocked';

export interface CanDeactivateComponent {
  unsubscribe: Subject<void>
  canDeactivate: () => boolean;
  save(): void;
  validForm(): boolean;
  disableForm(): boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UnsavedChangesGuard  {
  private dialog = inject(MatDialog);
  private notificationsService = inject(NotificationsService);


  /**
   * Prueft, ob eine Property tatsaechlich beschreibbar ist. Getter ohne Setter werfen
   * im strict-mode-Modul einen TypeError, wenn man ihnen etwas zuweist.
   */
  private isWritable(target: any, property: string): boolean {
    let current = target;
    while (current) {
      const descriptor = Object.getOwnPropertyDescriptor(current, property);
      if (descriptor) {
        return descriptor.set !== undefined || descriptor.writable === true;
      }
      current = Object.getPrototypeOf(current);
    }
    return false;
  }

  private markKnownFormsAsTouched(component: any): void {
    for (const formName of ['generalInfoForm', 'addressInfoForm', 'builderInfoForm', 'qualitySettingsInfoForm']) {
      component[formName]?.markAllAsTouched();
    }
  }

  canDeactivate(component: any, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if(component == null) {
      return true;
    }

    if(component.disableForm && component.disableForm()){
      if (component.unsubscribe) {
        component.unsubscribe.next();
      }
      return true;
    }

    // Check if there are unsaved changes (dirty forms or explicit isDirty flag)
    const hasChanges = component.isDirty ||
      (component.generalInfoForm && component.generalInfoForm.dirty) ||
      (component.addressInfoForm && component.addressInfoForm.dirty);

    const isInitCreateNavigation = component.isInit === true && nextState?.url?.startsWith('/organisations');

    // Waehrend der Anlage-Navigation gibt es keinen Verwerfen-Dialog, deshalb bleibt hier
    // der harte Block bestehen - sonst waeren die Eingaben unwiederbringlich weg.
    if (hasChanges && isInitCreateNavigation && component.validForm && !component.validForm()) {
      this.notificationsService.showErrorMessage('Änderungen nicht gespeichert, da nicht alle Pflichtfelder ausgefüllt sind!');
      this.markKnownFormsAsTouched(component);
      return false;
    }

    if (hasChanges && !isInitCreateNavigation) {
      // Unvollstaendige Pflichtfelder duerfen die Navigation nicht mehr hart blockieren:
      // Projekte ohne vollstaendige Adressdaten waren sonst dauerhaft eingesperrt, weil
      // validForm() nie true wird und der Verwerfen-Dialog gar nicht angeboten wurde. Wir
      // weisen nur noch darauf hin, dass nicht gespeichert werden kann, und lassen den
      // Nutzer dann regulaer verwerfen.
      if (component.validForm && !component.validForm()) {
        this.notificationsService.showErrorMessage('Änderungen können nicht gespeichert werden, da nicht alle Pflichtfelder ausgefüllt sind!');
        this.markKnownFormsAsTouched(component);
      }

      if (component.unsavedChangesDialogPromise) {
        return component.unsavedChangesDialogPromise;
      }

      component.dialogAlreadyShown = true;

      const dialogPromise: Promise<boolean | UrlTree> = new Promise((resolve) => {
        const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
          panelClass: "rounded-corners-dialog",
          data: {component: component},
          disableClose: true
        });
        dialogRef.afterClosed().subscribe(result => {
          component.dialogAlreadyShown = false;
          component.unsavedChangesDialogPromise = undefined;

          if (result != "discard") {
            // User cancelled - deny navigation
            resolve(false);
            return;
          }

          // Ab hier MUSS resolve() laufen. Faellt das Zuruecksetzen aus irgendeinem
          // Grund aus, wuerde das Promise sonst nie erfuellt und die Navigation haengt
          // dauerhaft - genau das passierte, als hier auf einen reinen Getter
          // (ProjectSettings.isDirty) geschrieben wurde und der TypeError den Callback
          // vor resolve() abbrach.
          let stay = false;
          try {
            // Call component's discard method if it exists
            if (component.discardChanges) {
              const outcome: DiscardOutcome | void = component.discardChanges();
              if (outcome === 'blocked') {
                // Die Komponente hat den Grund (fehlendes Pflichtfeld o. ae.) bereits gemeldet.
                this.notificationsService.showErrorMessage('Übernommene Änderungen konnten nicht gespeichert werden – bitte ergänzen und speichern.');
                stay = true;
                return;
              }
              this.notificationsService.showWarningMessage(outcome === 'savedAccepted'
                ? 'Übernommene Änderungen werden gespeichert, alle übrigen verworfen.'
                : 'Änderungen verworfen!');
            } else {
              this.notificationsService.showWarningMessage('Änderungen verworfen!');
              // Fallback: nur schreiben, wenn isDirty ein echtes Feld ist - Komponenten
              // mit Snapshot-Vergleich stellen isDirty als Getter ohne Setter bereit.
              if (this.isWritable(component, 'isDirty')) {
                component.isDirty = false;
              }
              // Fallback: mark forms as pristine
              if (component.generalInfoForm) {
                component.generalInfoForm.markAsPristine();
                component.generalInfoForm.markAsUntouched();
              }
              if (component.addressInfoForm) {
                component.addressInfoForm.markAsPristine();
                component.addressInfoForm.markAsUntouched();
              }
            }
            if (component.unsubscribe) {
              component.unsubscribe.next();
            }
          } catch (error) {
            console.error('Verwerfen der Änderungen fehlgeschlagen:', error);
          } finally {
            resolve(!stay);
          }
        })
      });

      component.unsavedChangesDialogPromise = dialogPromise;
      return dialogPromise;
    }

    // No unsaved changes, allow navigation
    if (component.unsubscribe) {
      component.unsubscribe.next();
    }
    return true;










    // console.log(component.canDeactivate());
    //
    // component.unsubscribe.next();
    //
    // if (!component.canDeactivate()) {
    //   if (component.validForm()) {
    //
    //     if(component.disableForm()) {
    //       return true;
    //     }else {
    //       component.save();
    //       this.snackbar.open('Änderungen gespeichert!', 'OK', {duration: 3000});
    //       return true;
    //     }
    //
    //   }else{
    //     this.snackbar.open('Änderungen nicht gespeichert, da nicht alle Pflichtfelder ausgefüllt sind!', 'OK', {duration: 5000});
    //     return false;
    //   }
    // }else {
    //     return component.canDeactivate();
    // }
  }
}
